import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { FiArrowLeft, FiPlus, FiSave, FiUpload } from 'react-icons/fi';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getApiUrl } from '../../config/api.config';

const FALLBACK_BRANDS = [
  'Maruti Suzuki', 'Hyundai', 'Tata Motors', 'Mahindra', 'Toyota', 'Kia', 'Honda',
];

const EditCar = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [brandCatalog, setBrandCatalog] = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadBrands = async () => {
      setBrandsLoading(true);
      try {
        const response = await fetch(getApiUrl('/car-brands'));
        if (cancelled) return;
        if (response.ok) {
          const json = await response.json();
          setBrandCatalog(Array.isArray(json?.data) ? json.data : []);
        }
      } catch (error) {
        console.warn('Failed to load brand catalog', error?.message || error);
      } finally {
        if (!cancelled) setBrandsLoading(false);
      }
    };
    loadBrands();
    return () => {
      cancelled = true;
    };
  }, []);
  const [formData, setFormData] = useState({
    brand: '',
    model: '',
    year: '',
    price: '',
    fuelType: '',
    transmission: '',
    kmDriven: '',
    mileage: '',
    location: '',
    description: '',
    color: '',
    customColor: '',
    features: [],
    // Private Details
    privateDetails: {
      ownerName: '',
      ownerPhone: '',
      ownerEmail: '',
      ownerAddress: '',
      insuranceProvider: '',
      insurancePolicyNumber: '',
      insuranceExpiryDate: '',
      rcNumber: '',
      chassisNumber: '',
      engineNumber: '',
      bankFinanced: false,
      bankName: '',
      loanOutstanding: '',
      noc: false,
      lastServiceDate: '',
      serviceCenter: '',
      additionalNotes: '',
      acquisitionDate: '',
      purchasePrice: ''
    }
  });

  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [imagePreview, setImagePreview] = useState([]);
  const [privateDocuments, setPrivateDocuments] = useState([]);
  const [existingPrivateDocuments, setExistingPrivateDocuments] = useState([]);
  const [privateDocPreview, setPrivateDocPreview] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customFeature, setCustomFeature] = useState('');
  const [showCustomFeatureInput, setShowCustomFeatureInput] = useState(false);

  const brands = useMemo(() => {
    if (brandCatalog.length === 0) return FALLBACK_BRANDS;
    return brandCatalog.map((entry) => entry.name);
  }, [brandCatalog]);

  const modelOptionsForSelectedBrand = useMemo(() => {
    if (!formData.brand || brandCatalog.length === 0) return [];
    const match = brandCatalog.find((entry) => entry.name === formData.brand);
    return match ? (match.models || []).map((m) => m.name) : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.brand, brandCatalog]);

  const fuelTypes = ['Petrol', 'Diesel', 'CNG', 'Electric', 'Hybrid'];
  const transmissionTypes = ['Manual', 'Automatic', 'CVT', 'AMT'];

  const colorOptions = [
    'White', 'Black', 'Silver', 'Grey', 'Red', 'Blue', 
    'Brown', 'Green', 'Orange', 'Yellow', 'Custom'
  ];

  const features = [
    'Air Conditioning', 'Power Steering', 'ABS', 'Airbags', 'Central Locking',
    'Power Windows', 'Music System', 'Bluetooth', 'GPS Navigation', 'Sunroof',
    'Leather Seats', 'Alloy Wheels', 'Fog Lights', 'Rear Parking Sensor'
  ];

  useEffect(() => {
    const fetchCarDetails = async () => {
      try {
        const token = localStorage.getItem('agent_token');
        console.log('🔍 Fetching car details for ID:', id);
        
        const response = await fetch(getApiUrl(`/cars/${id}`), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          console.log('📊 Car data received:', data);
          
          // Handle nested data structure - car might be in data.data.car or data.data
          const car = data.data?.car || data.data || data;
          console.log('🚗 Extracted car:', car);
          
          // Handle location object vs string
          let locationValue = '';
          if (typeof car.location === 'object' && car.location !== null) {
            locationValue = `${car.location.city}, ${car.location.state}`;
          } else if (typeof car.location === 'string') {
            locationValue = car.location;
          }
          
          setFormData({
            brand: car.brand || '',
            model: car.model || '',
            year: car.year || '',
            price: car.price || '',
            fuelType: car.fuelType || '',
            transmission: car.transmission || '',
            kmDriven: car.kmDriven || '',
            mileage: car.mileage || '',
            location: locationValue,
            description: car.description || '',
            color: car.color || '',
            customColor: '',
            features: car.features || [],
            privateDetails: {
              ownerName: car.privateDetails?.ownerName || '',
              ownerPhone: car.privateDetails?.ownerPhone || '',
              ownerEmail: car.privateDetails?.ownerEmail || '',
              ownerAddress: car.privateDetails?.ownerAddress || '',
              insuranceProvider: car.privateDetails?.insuranceProvider || '',
              insurancePolicyNumber: car.privateDetails?.insurancePolicyNumber || '',
              insuranceExpiryDate: car.privateDetails?.insuranceExpiryDate ? car.privateDetails.insuranceExpiryDate.split('T')[0] : '',
              rcNumber: car.privateDetails?.rcNumber || '',
              chassisNumber: car.privateDetails?.chassisNumber || '',
              engineNumber: car.privateDetails?.engineNumber || '',
              bankFinanced: car.privateDetails?.bankFinanced || false,
              bankName: car.privateDetails?.bankName || '',
              loanOutstanding: car.privateDetails?.loanOutstanding || '',
              noc: car.privateDetails?.noc || false,
              lastServiceDate: car.privateDetails?.lastServiceDate ? car.privateDetails.lastServiceDate.split('T')[0] : '',
              serviceCenter: car.privateDetails?.serviceCenter || '',
              additionalNotes: car.privateDetails?.additionalNotes || '',
              acquisitionDate: car.privateDetails?.acquisitionDate ? car.privateDetails.acquisitionDate.split('T')[0] : '',
              purchasePrice: car.privateDetails?.purchasePrice || ''
            }
          });
          
          console.log('✅ Form data set:', {
            brand: car.brand,
            model: car.model,
            location: locationValue
          });
          
          // Handle images - could be array of objects or array of strings
          const imageArray = car.images || [];
          const processedImages = imageArray.map(img => 
            typeof img === 'string' ? img : (img.url || img)
          );
          setExistingImages(processedImages);
          
          // Handle private documents
          const privateDocsArray = car.privateDocuments || [];
          setExistingPrivateDocuments(privateDocsArray);
          
        } else {
          const errorData = await response.json();
          console.error('❌ Failed to fetch car:', errorData);
          toast.error('Failed to fetch car details');
          navigate('/dashboard/cars');
        }
      } catch (error) {
        console.error('❌ Error fetching car:', error);
        toast.error('Error loading car details');
        navigate('/dashboard/cars');
      } finally {
        setLoading(false);
      }
    };

    fetchCarDetails();
  }, [id, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFeatureToggle = (feature) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature]
    }));
  };

  const handleAddCustomFeature = () => {
    if (customFeature.trim() && !formData.features.includes(customFeature.trim())) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, customFeature.trim()]
      }));
      setCustomFeature('');
      setShowCustomFeatureInput(false);
      toast.success(`Added custom feature: ${customFeature.trim()}`);
    } else if (formData.features.includes(customFeature.trim())) {
      toast.error('This feature already exists');
    }
  };

  const handleRemoveCustomFeature = (feature) => {
    // Only allow removing custom features (not predefined ones)
    const predefinedFeatures = [
      'Air Conditioning', 'Power Steering', 'ABS', 'Airbags', 'Central Locking',
      'Power Windows', 'Music System', 'Bluetooth', 'GPS Navigation', 'Sunroof',
      'Leather Seats', 'Alloy Wheels', 'Fog Lights', 'Rear Parking Sensor'
    ];
    
    if (!predefinedFeatures.includes(feature)) {
      setFormData(prev => ({
        ...prev,
        features: prev.features.filter(f => f !== feature)
      }));
      toast.info(`Removed custom feature: ${feature}`);
    }
  };

  const handlePrivateDetailsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      privateDetails: {
        ...prev.privateDetails,
        [name]: type === 'checkbox' ? checked : value
      }
    }));
  };

  const handlePrivateDocUpload = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate file size (5MB per file)
    const maxSize = 5 * 1024 * 1024;
    const invalidFiles = files.filter(file => file.size > maxSize);
    
    if (invalidFiles.length > 0) {
      toast.error(`Some files exceed 5MB limit: ${invalidFiles.map(f => f.name).join(', ')}`);
      return;
    }

    setPrivateDocuments(prev => [...prev, ...files]);

    const previews = files.map((file) => ({
      name: file.name,
      type: file.type,
      data: URL.createObjectURL(file),
    }));
    setPrivateDocPreview(prev => [...prev, ...previews]);

    toast.success(`Added ${files.length} private document(s)`);
  };

  const removePrivateDoc = (index, isExisting = false) => {
    if (isExisting) {
      setExistingPrivateDocuments(prev => prev.filter((_, i) => i !== index));
      toast.info('Private document removed');
    } else {
      setPrivateDocuments(prev => prev.filter((_, i) => i !== index));
      setPrivateDocPreview(prev => prev.filter((_, i) => i !== index));
      toast.info('Private document removed');
    }
  };


  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setImages(prev => [...prev, ...files]);

    const previews = files.map((file) => URL.createObjectURL(file));
    setImagePreview(prev => [...prev, ...previews]);
  };

  const removeNewImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreview(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (index) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.brand || !formData.model || !formData.year || !formData.price) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('agent_token');
      if (!token) {
        toast.error('Please login first. Redirecting to login page...');
        setIsSubmitting(false);
        setTimeout(() => navigate('/signin'), 1500);
        return;
      }

      // Parse location string to object
      let locationObj = {};
      if (formData.location) {
        const locationParts = formData.location.split(',').map(s => s.trim());
        if (locationParts.length >= 2) {
          locationObj = {
            city: locationParts[0],
            state: locationParts[1]
          };
        } else {
          // If no comma, assume it's just city
          locationObj = {
            city: formData.location,
            state: ''
          };
        }
      }

      const existingPrivateDocs = existingPrivateDocuments.map((doc, i) => ({
        url: typeof doc === 'string' ? doc : doc.url,
        name: doc?.name || `Private Document ${i + 1}`,
        type: doc?.type || 'image/jpeg',
      })).filter((doc) => !!doc.url);

      const existingImageUrls = existingImages
        .map((img, i) => ({
          url: typeof img === 'string' ? img : img?.url,
          isPrimary: i === 0,
        }))
        .filter((img) => !!img.url);

      const updateData = {
        ...formData,
        location: locationObj,
        features: formData.features,
        images: existingImageUrls,
        privateDocuments: existingPrivateDocs
      };

      // Handle color field - use customColor if color is "Other"
      if (formData.color === 'Other' && formData.customColor) {
        updateData.color = formData.customColor;
      }
      delete updateData.customColor;

      console.log('🔄 Updating car:', id);

      const multipartData = new FormData();
      Object.entries(updateData).forEach(([key, value]) => {
        if (value === undefined || value === null) return;

        if (typeof value === 'object') {
          multipartData.append(key, JSON.stringify(value));
        } else {
          multipartData.append(key, String(value));
        }
      });

      images.forEach((file) => {
        multipartData.append('images', file);
      });

      privateDocuments.forEach((file) => {
        multipartData.append('privateDocuments', file);
      });

      const response = await fetch(getApiUrl(`/cars/${id}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: multipartData,
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Car updated successfully:', result);
        toast.success('Car updated successfully!');
        setTimeout(() => navigate('/dashboard/cars'), 1000);
      } else {
        const errorData = await response.json();
        console.error('❌ Update failed:', errorData);
        toast.error(errorData.message || 'Failed to update car');
      }
    } catch (error) {
      console.error('❌ Error updating car:', error);
      toast.error('An error occurred while updating the car');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Edit Car</h1>
          <p className="mt-2 text-gray-600">Update car details and information</p>
        </div>
        <Link
          to="/dashboard/cars"
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
        >
          <FiArrowLeft />
          Back to Cars
        </Link>
      </motion.div>

      {/* Form */}
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        onSubmit={handleSubmit}
        className="bg-white rounded-xl shadow-sm p-6 space-y-6"
      >
        {/* Basic Information */}
        <div>
          <h2 className="text-xl font-semibold mb-4">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Brand *{brandsLoading ? <span className="text-xs text-gray-400 ml-2">(loading...)</span> : null}
              </label>
              <select
                name="brand"
                value={formData.brand}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    brand: e.target.value,
                    model: '',
                  }))
                }
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="">Select Brand</option>
                {brands.map(brand => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Model *
              </label>
              <select
                name="model"
                value={formData.model}
                onChange={handleInputChange}
                required
                disabled={!formData.brand || modelOptionsForSelectedBrand.length === 0}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
              >
                <option value="">
                  {!formData.brand
                    ? 'Pick a brand first'
                    : modelOptionsForSelectedBrand.length === 0
                    ? `No models configured for ${formData.brand}`
                    : 'Select Model'}
                </option>
                {modelOptionsForSelectedBrand.map((modelName) => (
                  <option key={modelName} value={modelName}>{modelName}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Year *
              </label>
              <input
                type="number"
                name="year"
                value={formData.year}
                onChange={handleInputChange}
                required
                min="1990"
                max={new Date().getFullYear() + 1}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="e.g., 2020"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price (₹) *
              </label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                required
                min="0"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="e.g., 550000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fuel Type *
              </label>
              <select
                name="fuelType"
                value={formData.fuelType}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="">Select Fuel Type</option>
                {fuelTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Transmission *
              </label>
              <select
                name="transmission"
                value={formData.transmission}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="">Select Transmission</option>
                {transmissionTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                KM Driven *
              </label>
              <input
                type="number"
                name="kmDriven"
                value={formData.kmDriven}
                onChange={handleInputChange}
                required
                min="0"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="e.g., 25000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location *
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="e.g., Mumbai, Maharashtra"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mileage (kmpl)
              </label>
              <input
                type="number"
                name="mileage"
                value={formData.mileage}
                onChange={handleInputChange}
                min="0"
                step="0.1"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="e.g., 18.5"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Color
              </label>
              <select
                name="color"
                value={formData.color}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="">Select Color</option>
                {colorOptions.map(color => (
                  <option key={color} value={color}>{color}</option>
                ))}
              </select>
              {formData.color === 'Other' && (
                <input
                  type="text"
                  name="customColor"
                  value={formData.customColor}
                  onChange={handleInputChange}
                  placeholder="Enter custom color"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent mt-2"
                />
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows="4"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            placeholder="Describe the car condition, features, etc..."
          />
        </div>

        {/* Features */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Features</h2>
            <motion.button
              type="button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowCustomFeatureInput(!showCustomFeatureInput)}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-orange-600 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors"
            >
              <FiPlus className="mr-1 h-4 w-4" />
              Add Custom Feature
            </motion.button>
          </div>

          {/* Custom Feature Input */}
          {showCustomFeatureInput && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-4 p-4 bg-orange-50 rounded-lg border border-orange-200"
            >
              <div className="flex gap-2">
                <input
                  type="text"
                  value={customFeature}
                  onChange={(e) => setCustomFeature(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddCustomFeature();
                    }
                  }}
                  placeholder="Enter custom feature name..."
                  className="flex-1 px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleAddCustomFeature}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                >
                  Add
                </motion.button>
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setShowCustomFeatureInput(false);
                    setCustomFeature('');
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </motion.button>
              </div>
            </motion.div>
          )}

          {/* Predefined Features */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {features.map(feature => (
              <label
                key={feature}
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={formData.features.includes(feature)}
                  onChange={() => handleFeatureToggle(feature)}
                  className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                />
                <span className="text-sm text-gray-700">{feature}</span>
              </label>
            ))}
          </div>

          {/* Custom Features Display */}
          {formData.features.filter(f => !features.includes(f)).length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Custom Features:</p>
              <div className="flex flex-wrap gap-2">
                {formData.features
                  .filter(f => !features.includes(f))
                  .map(feature => (
                    <motion.div
                      key={feature}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="inline-flex items-center px-3 py-1.5 bg-orange-100 text-orange-800 rounded-full text-sm"
                    >
                      <span>{feature}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveCustomFeature(feature)}
                        className="ml-2 text-orange-600 hover:text-orange-800"
                      >
                        ×
                      </button>
                    </motion.div>
                  ))}
              </div>
            </div>
          )}
        </div>

        {/* Private Details Section */}
        <div>
          <div className="mb-4">
            <div className="flex items-center space-x-2 mb-2">
              <h3 className="text-lg font-semibold text-gray-900">Private Details</h3>
              <span className="px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-full">
                🔒 Confidential - Not shown to buyers
              </span>
            </div>
            <p className="text-sm text-gray-600">
              This information is only visible to agents and admins. It will never be shown to buyers or on the public website.
            </p>
          </div>

          {/* Owner Details */}
          <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-6 border-2 border-red-200 mb-6">
            <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
              <span className="mr-2">👤</span> Owner Information
            </h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Owner Name
                </label>
                <input
                  type="text"
                  name="ownerName"
                  value={formData.privateDetails.ownerName}
                  onChange={handlePrivateDetailsChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Enter owner name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Owner Phone
                </label>
                <input
                  type="tel"
                  name="ownerPhone"
                  value={formData.privateDetails.ownerPhone}
                  onChange={handlePrivateDetailsChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Enter phone number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Owner Email
                </label>
                <input
                  type="email"
                  name="ownerEmail"
                  value={formData.privateDetails.ownerEmail}
                  onChange={handlePrivateDetailsChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Enter email address"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Owner Address
                </label>
                <input
                  type="text"
                  name="ownerAddress"
                  value={formData.privateDetails.ownerAddress}
                  onChange={handlePrivateDetailsChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Enter complete address"
                />
              </div>
            </div>
          </div>

          {/* Insurance Details */}
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-6 border-2 border-blue-200 mb-6">
            <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
              <span className="mr-2">🛡️</span> Insurance Details
            </h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Insurance Provider
                </label>
                <input
                  type="text"
                  name="insuranceProvider"
                  value={formData.privateDetails.insuranceProvider}
                  onChange={handlePrivateDetailsChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="e.g., ICICI Lombard"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Policy Number
                </label>
                <input
                  type="text"
                  name="insurancePolicyNumber"
                  value={formData.privateDetails.insurancePolicyNumber}
                  onChange={handlePrivateDetailsChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Enter policy number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Insurance Expiry Date
                </label>
                <input
                  type="date"
                  name="insuranceExpiryDate"
                  value={formData.privateDetails.insuranceExpiryDate}
                  onChange={handlePrivateDetailsChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
            </div>
          </div>

          {/* Vehicle Documents */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border-2 border-green-200 mb-6">
            <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
              <span className="mr-2">📄</span> Vehicle Documents
            </h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  RC Number
                </label>
                <input
                  type="text"
                  name="rcNumber"
                  value={formData.privateDetails.rcNumber}
                  onChange={handlePrivateDetailsChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Enter RC number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chassis Number
                </label>
                <input
                  type="text"
                  name="chassisNumber"
                  value={formData.privateDetails.chassisNumber}
                  onChange={handlePrivateDetailsChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Enter chassis number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Engine Number
                </label>
                <input
                  type="text"
                  name="engineNumber"
                  value={formData.privateDetails.engineNumber}
                  onChange={handlePrivateDetailsChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Enter engine number"
                />
              </div>
            </div>
          </div>

          {/* Finance Details */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border-2 border-purple-200 mb-6">
            <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
              <span className="mr-2">🏦</span> Finance Details
            </h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  name="bankFinanced"
                  checked={formData.privateDetails.bankFinanced}
                  onChange={handlePrivateDetailsChange}
                  className="rounded border-gray-300 text-orange-600 focus:ring-orange-500 h-5 w-5"
                />
                <label className="text-sm font-medium text-gray-700">
                  Bank Financed
                </label>
              </div>
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  name="noc"
                  checked={formData.privateDetails.noc}
                  onChange={handlePrivateDetailsChange}
                  className="rounded border-gray-300 text-orange-600 focus:ring-orange-500 h-5 w-5"
                />
                <label className="text-sm font-medium text-gray-700">
                  NOC Available
                </label>
              </div>
            </div>
            {formData.privateDetails.bankFinanced && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="grid grid-cols-1 gap-4 sm:grid-cols-2 mt-4"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bank Name
                  </label>
                  <input
                    type="text"
                    name="bankName"
                    value={formData.privateDetails.bankName}
                    onChange={handlePrivateDetailsChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Enter bank name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Loan Outstanding (₹)
                  </label>
                  <input
                    type="number"
                    name="loanOutstanding"
                    value={formData.privateDetails.loanOutstanding}
                    onChange={handlePrivateDetailsChange}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Enter outstanding amount"
                  />
                </div>
              </motion.div>
            )}
          </div>

          {/* Service & Purchase History */}
          <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-6 border-2 border-yellow-200 mb-6">
            <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
              <span className="mr-2">🔧</span> Service & Purchase History
            </h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Service Date
                </label>
                <input
                  type="date"
                  name="lastServiceDate"
                  value={formData.privateDetails.lastServiceDate}
                  onChange={handlePrivateDetailsChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service Center
                </label>
                <input
                  type="text"
                  name="serviceCenter"
                  value={formData.privateDetails.serviceCenter}
                  onChange={handlePrivateDetailsChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Enter service center name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Acquisition Date
                </label>
                <input
                  type="date"
                  name="acquisitionDate"
                  value={formData.privateDetails.acquisitionDate}
                  onChange={handlePrivateDetailsChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Purchase Price (₹)
                </label>
                <input
                  type="number"
                  name="purchasePrice"
                  value={formData.privateDetails.purchasePrice}
                  onChange={handlePrivateDetailsChange}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Enter purchase price"
                />
              </div>
            </div>
          </div>

          {/* Additional Notes */}
          <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl p-6 border-2 border-gray-200">
            <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
              <span className="mr-2">📝</span> Additional Private Notes
            </h4>
            <textarea
              name="additionalNotes"
              value={formData.privateDetails.additionalNotes}
              onChange={handlePrivateDetailsChange}
              rows={4}
              maxLength={1000}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="Enter any additional private notes or observations about the car, owner, or transaction..."
            />
            <p className="mt-2 text-xs text-gray-500">
              {formData.privateDetails.additionalNotes.length}/1000 characters
            </p>
          </div>
        </div>

        {/* Existing Private Documents */}
        {existingPrivateDocuments.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <h3 className="text-lg font-semibold text-gray-900">Existing Private Documents</h3>
                <span className="px-2 py-1 text-xs font-medium text-red-700 bg-red-100 border border-red-300 rounded-full flex items-center">
                  🔒 Confidential
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-200 rounded-xl p-6">
              {existingPrivateDocuments.map((doc, index) => (
                <div key={index} className="relative group bg-white rounded-lg border-2 border-red-200 p-3">
                  {doc.type?.startsWith('image/') ? (
                    <img
                      src={doc.url}
                      alt={doc.name}
                      className="w-full h-32 object-cover rounded"
                    />
                  ) : (
                    <div className="w-full h-32 flex flex-col items-center justify-center bg-red-50 rounded">
                      <svg className="w-12 h-12 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                      </svg>
                      <p className="text-xs text-gray-600 mt-2 text-center">PDF Document</p>
                    </div>
                  )}
                  <p className="text-xs text-gray-600 mt-2 truncate" title={doc.name}>
                    {doc.name}
                  </p>
                  <button
                    type="button"
                    onClick={() => removePrivateDoc(index, true)}
                    className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    ×
                  </button>
                  <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-red-600 text-white text-xs rounded-full">
                    Private
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* New Private Documents */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <h3 className="text-lg font-semibold text-gray-900">Add New Private Documents</h3>
              <span className="px-2 py-1 text-xs font-medium text-red-700 bg-red-100 border border-red-300 rounded-full flex items-center">
                🔒 Confidential - Agents Only
              </span>
            </div>
            {privateDocuments.length > 0 && (
              <span className="px-3 py-1 text-sm font-medium text-red-600 bg-red-50 rounded-full">
                {privateDocuments.length} new {privateDocuments.length === 1 ? 'document' : 'documents'}
              </span>
            )}
          </div>
          <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-200 rounded-xl p-6">
            <input
              type="file"
              multiple
              accept="image/*,application/pdf"
              onChange={handlePrivateDocUpload}
              className="hidden"
              id="private-doc-upload"
            />
            <label
              htmlFor="private-doc-upload"
              className="flex flex-col items-center cursor-pointer border-2 border-dashed border-red-300 rounded-lg p-8 bg-white hover:bg-red-50 transition-colors"
            >
              <FiUpload className="h-12 w-12 text-red-400 mb-2" />
              <span className="text-gray-600 font-medium">Click to upload private documents</span>
              <span className="text-sm text-gray-400 mt-1">PDF, PNG, JPG up to 5MB</span>
              <span className="text-xs text-red-600 font-semibold mt-2">🔒 Never shown to buyers</span>
            </label>

            {privateDocPreview.length > 0 && (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 mt-4">
                {privateDocPreview.map((doc, index) => (
                  <div key={index} className="relative group bg-white rounded-lg border-2 border-red-200 p-3">
                    {doc.type?.startsWith('image/') ? (
                      <img
                        src={doc.data}
                        alt={doc.name}
                        className="w-full h-32 object-cover rounded"
                      />
                    ) : (
                      <div className="w-full h-32 flex flex-col items-center justify-center bg-red-50 rounded">
                        <svg className="w-12 h-12 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                        </svg>
                        <p className="text-xs text-gray-600 mt-2 text-center">PDF Document</p>
                      </div>
                    )}
                    <p className="text-xs text-gray-600 mt-2 truncate" title={doc.name}>
                      {doc.name}
                    </p>
                    <button
                      type="button"
                      onClick={() => removePrivateDoc(index, false)}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      ×
                    </button>
                    <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-red-600 text-white text-xs rounded-full">
                      Private
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Existing Images */}
        {existingImages.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold mb-4">Existing Images</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {existingImages.map((image, index) => (
                <div key={index} className="relative group">
                  <img
                    src={image}
                    alt={`Existing ${index + 1}`}
                    className="w-full h-32 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => removeExistingImage(index)}
                    className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* New Images */}
        <div>
          <h2 className="text-xl font-semibold mb-4">Add New Images</h2>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              id="image-upload"
            />
            <label
              htmlFor="image-upload"
              className="flex flex-col items-center cursor-pointer"
            >
              <FiUpload className="h-12 w-12 text-gray-400 mb-2" />
              <span className="text-gray-600">Click to upload images</span>
              <span className="text-sm text-gray-400 mt-1">
                PNG, JPG up to 10MB
              </span>
            </label>
          </div>

          {imagePreview.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              {imagePreview.map((preview, index) => (
                <div key={index} className="relative group">
                  <img
                    src={preview}
                    alt={`Preview ${index + 1}`}
                    className="w-full h-32 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => removeNewImage(index)}
                    className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex justify-end gap-4 pt-6 border-t">
          <Link
            to="/dashboard/cars"
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex items-center gap-2 px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiSave />
            {isSubmitting ? 'Updating...' : 'Update Car'}
          </button>
        </div>
      </motion.form>
    </div>
  );
};

export default EditCar;
